import { Option } from '@sapphire/result';
import { BaseMapper } from '@ps/utils';
import {
  SupplierAllocation,
  SupplierAllocationBase,
  SupplierAllocationIntent,
  SupplierAllocationMember,
} from '@ps/survey-utils';

import {
  SupplierAllocationDto,
  SupplierAllocationIntentDto,
  SupplierAllocationMemberDto,
} from '../dtos/supplier.dto';

export class SupplierAllocationMapper extends BaseMapper<
  SupplierAllocationDto,
  SupplierAllocation
> {
  /**
   * A non-empty `group_ref` alone isn't enough — the backend also sends a
   * standalone row per group member (same `group_ref`, but a single-entry
   * `suppliers[]`), so requiring more than one nested supplier is what
   * actually distinguishes the real group row from those duplicates. An
   * ungrouped allocation always carries exactly one supplier, which supplies
   * both its id and its name.
   */
  public fromDto(dto: SupplierAllocationDto): SupplierAllocation {
    const base = this._mapBase(dto);

    return Option.from(dto.group_ref)
      .filter((groupRef) => groupRef.length > 0 && dto.suppliers.length > 1)
      .match({
        some: (groupId): SupplierAllocation => {
          const groupName = Option.from(dto.group_name).unwrapOr('');
          return {
            ...base,
            name: groupName,
            isGroup: true,
            groupId,
            groupName,
          };
        },
        none: (): SupplierAllocation => {
          const [supplier] = dto.suppliers;
          return {
            ...base,
            name: supplier.name,
            isGroup: false,
            id: supplier.id,
          };
        },
      });
  }

  private _mapBase(
    dto: SupplierAllocationDto,
  ): Omit<SupplierAllocationBase, 'name'> {
    return {
      completesGoal: dto.completes_goal,
      currentTarget: dto.current_target,
      remaining: dto.remaining,
      fielded: dto.fielded,
      currencySymbol: dto.currency_symbol,
      cpi: dto.cpi,
      bidCpi: dto.bid_cpi,
      blockPrice: dto.block_price,
      buyerInProgress: dto.buyer_in_progress,
      lastStartDate: dto.last_start_date,
      lastCompleteDate: dto.last_complete_date,
      intent: this._mapIntent(dto.intent),
      suppliers: this._mapMembers(dto.suppliers),
      isPdsApplied: dto.is_pds_applied,
      fee: dto.fee,
      flexValue: dto.flexValue,
      quantity: dto.quantity,
      groupPercentage: dto.groupPercentage,
      isActive: dto.is_active,
      psStartCount24: dto.ps_start_count_24,
      estimateEpc: dto.estimate_epc,
      actualEpc: dto.actual_epc,
      platformEpc: dto.platform_epc,
      validClicks: dto.valid_clicks,
      validEpc: dto.valid_epc,
      validClickPercentage: dto.valid_click_percentage,
      totalEpc: dto.total_epc,
      srvStartCount: dto.srv_start_count,
      clickBalance: dto.click_balance,
      maxCpi: dto.max_cpi,
      conversionRate: dto.conversion_rate,
    };
  }

  private _mapIntent(
    dto: SupplierAllocationIntentDto,
  ): SupplierAllocationIntent {
    return {
      intentCount: dto.intent_count,
      intentNotSet: dto.intent_not_set,
      intentDenied: dto.intent_denied,
      intentAccepted: dto.intent_accepted,
      childIntentStats: dto.child_intent_stats,
    };
  }

  private _mapMembers(
    dtos: SupplierAllocationMemberDto[],
  ): SupplierAllocationMember[] {
    return dtos.map((dto) => ({
      id: dto.id,
      name: dto.name,
      allocation: dto.allocation,
    }));
  }
}
